import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { API } from '@/api'
import { Button, Loading, Modal, Pagination, Trans } from '@/components'
import { Divider, message } from '@/components'
import { TeamRole } from '@/consts/team'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'

import { AddMemberDialog } from './components/AddMemberDialog'
import { AssignCreditDialog, type MemberTarget } from './components/AssignCreditDialog'
import { BulkDropdown } from './components/BulkDropdown'
import { FiltersBar } from './components/FilterDropDown'
import { MemberDetailPage } from './components/MemberDetailPage'
import { type MemberRow as Row, MemberTable } from './components/MemberTable'
import { ReclaimCreditDialog } from './components/ReclaimCreditDialog'
import { StatsCards } from './components/StateCard'
import { useTeamMembers } from './hooks/useTeamMembers'
import { assignCredits, createMemberFlow, reclaimCredits } from './services/member'
import { removeMember, removeMembers, suspendMembers, unSuspendMembers } from './services/member'
// import { getTeamInfo, getTeamMemberList } from './services/team'
import { AddMemberForm, NewMemberResult } from './types/types'

export function MemberDetailPageWrapper() {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [raw, setRaw] = useState<any>(null)
  const teamResID = useTeamStore((s) => s.teamTokenData?.teamResID)

  useEffect(() => {
    let alive = true
    ;(async () => {
      if (!teamResID) return
      if (!id) return
      setLoading(true)
      try {
        const detail = await API.getTeamMember({ res_id: id })
        const AllMemberInstances = await API.getTeamStatMemberInstances({
          res_id: teamResID,
          page: 1,
          per_page: 100,
        })
        const personalInstances =
          AllMemberInstances.stats.find((stat) => stat.user.resID === id)?.unfinishedCount ?? 0
        const memberACl = await API.getTeamMemberResourceACL({ res_id: id })
        const totalAssignedInstances =
          memberACl.acl.quotas?.items.find((q) => q.QuotaType === 3)?.QuotaValue ?? 0
        if (!alive) return
        setRaw({ res_id: id, resID: id, ...detail, totalAssignedInstances, personalInstances })
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [id])

  if (!id) return <div>Not found</div>
  if (loading || !raw)
    return (
      <div className="p-8">
        <Loading />
      </div>
    )

  return <MemberDetailPage raw={raw} />
}

export function MemberPage() {
  const { t } = useTranslation()
  const userStore = useUserStore()
  const teamResID = useTeamStore((s) => s.teamTokenData?.teamResID)
  const currentUserRole = useTeamStore((s) => s.teamTokenData?.userRole)

  const {
    pageRows,
    total,
    page,
    setPage,
    pageSize,
    filters,
    setFilters,
    currentPageSelectedRowKeys,
    onSelectionChange,
    selectedIds,
    stats,
    loading,
    allRows,
    setAllRows,
    teamDomain,
    sortKey,
    sortOrder,
    onSortChange,
    refresh,
  } = useTeamMembers(teamResID, 10)

  const [addOpen, setAddOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignVariant, setAssignVariant] = useState<'bulk' | 'single'>('bulk')
  const [assignTargets, setAssignTargets] = useState<MemberTarget[]>([])
  const [reclaimOpen, setReclaimOpen] = useState(false)
  const [reclaimTarget, setReclaimTarget] = useState<MemberTarget | null>(null)
  const [adminBalance, setAdminBalance] = useState(0) // TODO: from API
  const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds])

  const isCreditManager = (row?: Pick<Row, 'role' | 'name'>) => {
    if (!row || row.role == null) return false
    if (currentUserRole == null) return false
    if (currentUserRole !== TeamRole.Owner && currentUserRole !== TeamRole.Admin) return false
    if (row.name === userStore.name) return false
    return true
  }

  const canAssignCredits = (row?: Pick<Row, 'role' | 'name'>) => {
    if (!isCreditManager(row)) return false
    return row!.role >= currentUserRole!
  }

  const canReclaimCredits = (row?: Pick<Row, 'role' | 'name'>) => {
    if (!isCreditManager(row)) return false
    return row!.role > currentUserRole!
  }

  const selectedAssignableIdsArray = useMemo(
    () =>
      selectedIdsArray.filter((id) => {
        const row = allRows.find((item) => item.id === id)
        return canAssignCredits(row)
      }),
    [allRows, currentUserRole, selectedIdsArray, userStore.name],
  )

  const getAdminBalance = () => {
    const res = userStore.account.totalBonus.moneyCent ?? 0
    setAdminBalance(res)
  }

  function buildSuspendConfirmText(ids: string[], allRows: Row[]) {
    const names = allRows.filter((r) => ids.includes(r.id)).map((r) => r.username || r.id)
    if (names.length <= 5) return `Suspend these accounts: ${names.join(', ')}?`
    const head = names.slice(0, 5).join(', ')
    return `Suspend these ${ids.length} accounts: ${head} and ${names.length - 5} more?`
  }

  useEffect(() => {
    getAdminBalance()
  }, [userStore.account.totalBonus.moneyCent])

  const openAssignBulk = (ids: string[]) => {
    if (!ids.length) return
    const targets: MemberTarget[] = allRows
      .filter((r) => ids.includes(r.id) && canAssignCredits(r))
      .map((r) => ({ id: r.id, name: r.username, currentCredit: r.balance }))
    if (!targets.length) return
    setAssignVariant('bulk')
    setAssignTargets(targets)
    setAssignOpen(true)
  }
  const openAssignSingle = (id: string) => {
    const r = allRows.find((x) => x.id === id)
    if (!r || !canAssignCredits(r)) return
    setAssignVariant('single')
    setAssignTargets([{ id: r.id, name: r.username, currentCredit: r.balance }])
    setAssignOpen(true)
  }

  const openReclaimSingle = (id: string) => {
    const r = allRows.find((x) => x.id === id)
    if (!r || !canReclaimCredits(r)) return
    setReclaimTarget({ id: r.id, name: r.username, currentCredit: r.balance })
    setReclaimOpen(true)
  }

  const handleReclaimConfirm = async (payload: { memberId: string; reclaimAmount: number }) => {
    const { memberId, reclaimAmount } = payload
    if (!teamResID) return

    try {
      await reclaimCredits(teamResID, memberId, reclaimAmount)

      await Promise.all([userStore.getAccount(), refresh()])

      setReclaimOpen(false)
      message.success('Credits reclaimed successfully.')
    } catch (e) {
      console.error('reclaimCredits failed:', e)
    }
  }

  const bulkSuspend = async (ids: string[]) => {
    if (!ids.length) return
    try {
      await Modal.confirm({
        title: <Trans i18nKey="team.member.dialog.suspend.title">Suspend Accounts</Trans>,
        className: 'w-[480px]',
        content: buildSuspendConfirmText(ids, allRows),
        confirmBtnCfg: {
          type: 'warning',
          text: t('team.member.dialog.suspend.confirm', 'Suspend'),
        },
      })

      const { amountOfSuccess, amountOfFailure } = await suspendMembers(ids)
      if (amountOfSuccess > 0) {
        const okSet = new Set(ids.slice(0, amountOfSuccess))
        setAllRows((prev) => prev.map((r) => (okSet.has(r.id) ? { ...r, status: 'inactive' } : r)))
      }

      if (amountOfFailure === 0) message.success(`Suspended ${amountOfSuccess} account(s).`)
      else if (amountOfSuccess === 0) message.error('Failed to suspend the selected account(s).')
      else message.error(`Suspended ${amountOfSuccess}, failed ${amountOfFailure}.`)
    } catch {
      message.error('Suspend failed.')
    }
  }

  const bulkDelete = async (ids: string[]) => {
    if (ids.length === 0) return

    const names = allRows.filter((r) => ids.includes(r.id)).map((r) => r.username)
    const listPreview =
      names.length <= 5
        ? names.join(', ')
        : `${names.slice(0, 5).join(', ')} … and ${names.length - 5} more`

    try {
      await Modal.confirm({
        title: 'Delete Accounts',
        className: 'w-[480px]',
        content: (
          <div>
            <div className="mb-2">Are you sure you want to delete the selected accounts?</div>
            <div className="text-secondary break-words">{listPreview}</div>
            <div className="mt-2 text-error t-sm">This action cannot be undone.</div>
          </div>
        ),
        confirmBtnCfg: { type: 'danger', text: 'Delete Accounts' },
      })

      const { amountOfSuccess, amountOfFailure } = await removeMembers(ids)

      if (amountOfSuccess.length) {
        setAllRows((prev) => prev.filter((r) => !amountOfSuccess.includes(r.id)))
        message.success(`Deleted ${amountOfSuccess.length} account(s).`)
      }
      if (amountOfFailure.length) {
        const failedNames = allRows
          .filter((r) => amountOfFailure.some((f) => f.id === r.id))
          .map((r) => r.username)
        message.error(
          `Failed to delete ${amountOfFailure.length} account(s): ${failedNames.join(', ')}`,
        )
      }
    } catch {
      message.error('Delete failed.')
    }
  }
  const rowAssign = (id: string) => openAssignSingle(id)
  const rowReclaim = (id: string) => openReclaimSingle(id)
  const rowSuspendToggle = async (id: string, isCurrentlyInactive: boolean) => {
    const name = allRows.find((r) => r.id === id)?.username || id
    const target = allRows.find((r) => r.id === id)

    if (target?.name === userStore.name) return

    if (isCurrentlyInactive) {
      try {
        await Modal.confirm({
          title: <Trans i18nKey="team.member.dialog.reactivate.title">Reactivate Account</Trans>,
          className: 'w-[480px]',
          content: (
            <Trans i18nKey="team.member.dialog.reactivate.content" values={{ name }}>
              Reactivate {{ name }}?
            </Trans>
          ),
          confirmBtnCfg: {
            type: 'primary',
            text: <Trans i18nKey="team.member.memberInfo.unsuspend">Unsuspend</Trans>,
          },
        })
        const { amountOfSuccess } = await unSuspendMembers([id])
        if (amountOfSuccess > 0) {
          setAllRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'active' } : r)))
          message.success(
            (
              <Trans i18nKey="team.member.memberInfo.toast.accountUnsuspended">
                Account reactivated.
              </Trans>
            ) as any,
          )
        } else {
          message.error(
            (
              <Trans i18nKey="team.member.memberInfo.toast.accountUnsuspendFailed">
                Unsuspend failed.
              </Trans>
            ) as any,
          )
        }
      } catch {
        console.warn('Cancel by user.')
      }
      return
    }

    try {
      await Modal.confirm({
        title: <Trans i18nKey="team.member.dialog.suspend.title">Suspend Account</Trans>,
        className: 'w-[480px]',
        content: (
          <Trans i18nKey="team.member.dialog.suspend.content" values={{ name }}>
            Suspend {{ name }}?
          </Trans>
        ),
        confirmBtnCfg: {
          type: 'warning',
          text: t('team.member.dialog.suspend.confirm', 'Suspend'),
        },
      })
      const { amountOfSuccess } = await suspendMembers([id])
      if (amountOfSuccess > 0) {
        setAllRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'inactive' } : r)))
        message.success(
          (
            <Trans i18nKey="team.member.memberInfo.toast.accountSuspended">
              Account suspended.
            </Trans>
          ) as any,
        )
      } else {
        message.error(
          (
            <Trans i18nKey="team.member.memberInfo.toast.accountSuspendedFailed">
              Suspend failed.
            </Trans>
          ) as any,
        )
      }
    } catch {
      console.warn('Cancel by user.')
    }
    return
  }

  const rowDelete = async (id: string) => {
    if (!teamResID) return console.error('No team context')
    const target = allRows.find((x) => x.id === id)
    const name = target?.username ?? id
    if (target?.name === userStore.name) return
    if (target?.role === TeamRole.Normal) {
      try {
        await Modal.confirm({
          title: <Trans i18nKey="team.member.dialog.delete.title">Delete Account</Trans>,
          className: 'w-[480px]',
          content: (
            <div>
              <div className="mb-2">
                <Trans i18nKey="team.member.dialog.delete.content" values={{ name }}>
                  Are you sure you want to delete <span className="text-highlight">{{ name }}</span>{' '}
                  account?
                </Trans>
              </div>
              <div className="mt-2 text-error t-sm">
                <Trans i18nKey="team.member.dialog.delete.content2">
                  This action cannot be undone.
                </Trans>
              </div>
            </div>
          ),
          confirmBtnCfg: {
            type: 'danger',
            text: t('team.member.dialog.delete.confirm', 'Delete Account'),
          },
        })

        await removeMember(teamResID, id)
        setAllRows((prev) => prev.filter((r) => r.id !== id))

        await userStore.getAccount()

        message.success(
          (
            <Trans i18nKey="team.member.memberInfo.toast.accountDeleted">Account deleted.</Trans>
          ) as any,
        )
      } catch {
        console.warn('Cancel by user.')
      }
    } else {
      message.error(
        (
          <Trans i18nKey="team.member.memberInfo.toast.accountDeleteError">
            Only normal members can be deleted. Please Downgrade to normal member first.
          </Trans>
        ) as any,
      )
    }
  }

  const createMember = async (form: AddMemberForm): Promise<NewMemberResult> => {
    if (!teamResID) throw new Error('No team context')
    if (!teamDomain) throw new Error('No team domain')

    const { resultForDialog, memberRow } = await createMemberFlow(teamResID, teamDomain, form)

    if (memberRow) {
      const initCredit = form.assignCredit ?? 0
      const initCreditCent = Math.round(initCredit * 100)

      const patchedRow = {
        ...memberRow,
        balance: (memberRow.balance ?? 0) + initCreditCent,
      }

      setAllRows((prev) => {
        const idx = prev.findIndex((x) => x.id === patchedRow.id)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = patchedRow
          return next
        }
        return [patchedRow, ...prev]
      })
    }

    return resultForDialog
  }

  const handleAssignConfirm = async (payload: {
    perMemberAmount: number
    memberIds: string[]
    totalCost: number
  }) => {
    const { perMemberAmount, memberIds, totalCost } = payload
    if (!teamResID) return

    try {
      await assignCredits(teamResID, memberIds, perMemberAmount)

      const deltaCent = Math.round(perMemberAmount * 100)
      setAllRows((prev) =>
        prev.map((r) => (memberIds.includes(r.id) ? { ...r, balance: r.balance + deltaCent } : r)),
      )

      setAdminBalance((prev) => Number((prev - totalCost).toFixed(3)))

      setAssignOpen(false)
    } catch (e) {
      console.error('assignCredits failed:', e)
    }
  }

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div className="h6">
          <Trans i18nKey="team.member.title">Member</Trans>
        </div>
      </div>
      <Divider />

      <StatsCards
        activeMembers={stats.activeMembers}
        inactiveMembers={stats.inactiveMembers}
        administratorNumbers={stats.administratorNumbers}
        totalTeamSeats={stats.totalTeamSeats}
      />

      <FiltersBar
        status={filters.status}
        bind={filters.bind}
        search={filters.search}
        onStatusChange={(v) => setFilters((f) => ({ ...f, status: v }))}
        onBindChange={(v) => setFilters((f) => ({ ...f, bind: v }))}
        onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
        right={
          <>
            <BulkDropdown
              disabled={selectedAssignableIdsArray.length === 0}
              onAssignCredits={() => openAssignBulk(selectedAssignableIdsArray)}
              onSuspendAccount={() => bulkSuspend(selectedIdsArray)}
              onDeleteAccounts={() => bulkDelete(selectedIdsArray)}
            />
            <Button type="secondary" onClick={() => setAddOpen(true)}>
              <Trans i18nKey="team.member.addMembers">Add Members</Trans>
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : (
        <div className="mt-4">
          <MemberTable
            rows={pageRows}
            loading={loading}
            selectedRowKeys={currentPageSelectedRowKeys}
            onSelectionChange={onSelectionChange}
            onRowAssign={rowAssign}
            onRowReclaim={rowReclaim}
            onRowSuspendToggle={rowSuspendToggle}
            onRowDelete={rowDelete}
            sticky
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />

          <Pagination
            size={pageSize}
            total={total}
            current={page}
            className="justify-end mt-4"
            onChange={(p) => setPage(p)}
          />
        </div>
      )}

      <AddMemberDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        adminBalance={adminBalance}
        createMember={createMember}
        teamDomain={teamDomain}
        onCreated={() => {}}
      />

      <AssignCreditDialog
        open={assignOpen}
        variant={assignVariant}
        adminBalance={adminBalance}
        targets={assignTargets}
        defaultAmount={0}
        onClose={() => setAssignOpen(false)}
        onConfirm={handleAssignConfirm}
      />

      <ReclaimCreditDialog
        open={reclaimOpen}
        target={reclaimTarget}
        onClose={() => setReclaimOpen(false)}
        onConfirm={handleReclaimConfirm}
      />
    </div>
  )
}
